'use client'

import { useEffect, useState } from 'react'
import { captureEvent, EVENTS } from '@/lib/posthog/events'
import { createClient } from '@/lib/supabase/client'
import { getSiteURL } from '@/utils/siteUrl'
import { safeRedirect } from '@/utils/safeRedirect'
import { suggestEmailFix } from '@/utils/emailTypoSuggest'
import { Eye, EyeOff } from 'lucide-react'
import Link from 'next/link'
import { SHARPR_WORDMARK_DIMS } from '@/lib/brand'
import WelcomePanel from './WelcomePanel'
import { CaptchaWidget } from '@/components/client/CaptchaWidget'
import { useCaptcha } from '@/hooks/useCaptcha'
import { storeShareId } from '@/utils/shareIdStorage'
import { storeSlipDestination, type SlipDestination } from '@/utils/slipDestinationStorage'
import { getStoredReferralCode } from '@/utils/referralStorage'
import { setOAuthIntent } from '@/lib/auth/oauth-intent.client';
import { armWelcomeTour } from '@/components/client/tour/welcomeTourFlag'
import { signInOrOutHere } from '@/lib/auth/thisTab'
import { pushSignupToLoops } from './actions'

// Shared input chrome — matches the email/password fields 1:1
const INPUT_CLASS =
  'w-full px-3 py-2 bg-white dark:bg-bg-tertiary border border-border dark:border-border-secondary rounded-md text-fg dark:text-text-primary placeholder:text-muted-foreground dark:placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 transition-all duration-200'

const LABEL_CLASS =
  'block text-sm font-medium text-fg dark:text-text-primary mb-1.5'

interface PasswordFieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  visible: boolean
  onToggleVisible: () => void
  disabled: boolean
  autoComplete?: string
  children?: React.ReactNode
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  disabled,
  autoComplete = 'new-password',
  children,
}: PasswordFieldProps) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          minLength={8}
          autoComplete={autoComplete}
          spellCheck={false}
          className={`${INPUT_CLASS} pr-10`}
          placeholder="••••••••"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-text-tertiary hover:text-fg dark:hover:text-text-primary transition-colors"
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      {children}
    </div>
  )
}

function getSubmitLabel({ loading }: { loading: boolean }): string {
  if (loading) return 'Continuing...'
  return 'Continue'
}

interface SignUpFormProps {
  redirectTo: string | null
  prefillEmail: string | null
  shareId?: string | null
  /** Shared-slip destination to persist for post-checkout deep-link.
   *  Parallel to `shareId` — that's attribution; this is where-to-land. */
  slipDestination?: SlipDestination | null
}

export default function SignUpForm({ redirectTo, prefillEmail, shareId, slipDestination }: SignUpFormProps) {
  const [email, setEmail] = useState(prefillEmail || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Inline email typo suggestion — Baymard-preferred alternative to a Confirm Email
  // field. Surfaces "Did you mean gmail.com?" when a popular domain is close-but-wrong.
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null)
  const captcha = useCaptcha()
  const supabase = createClient()

  // Persist a shared-slip attribution ID across the signup → checkout boundary.
  // Survives OAuth round-trips because it lives in localStorage, parallel to referral_code.
  useEffect(() => {
    if (shareId) storeShareId(shareId)
  }, [shareId])

  // Persist the slip *destination* (player + market + book) in parallel —
  // consumed by PlayerSelectionView on post-checkout app landing to open
  // the line-movement overlay anchored to the originally-shared pick.
  useEffect(() => {
    if (slipDestination) storeSlipDestination(slipDestination)
  }, [slipDestination])

  // Build login URL with redirect parameter
  const loginUrl = redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'

  // Calculate password strength
  const getPasswordStrength = (pwd: string): { score: number; label: string; color: string; textColor: string } => {
    if (!pwd) return { score: 0, label: '', color: '', textColor: '' }

    let score = 0

    // Length check
    if (pwd.length >= 8) score++
    if (pwd.length >= 12) score++

    // Character variety checks
    if (/[a-z]/.test(pwd)) score++ // lowercase
    if (/[A-Z]/.test(pwd)) score++ // uppercase
    if (/[0-9]/.test(pwd)) score++ // numbers
    if (/[^a-zA-Z0-9]/.test(pwd)) score++ // special chars

    // Determine strength
    if (score <= 2) {
      return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600 dark:text-red-400' }
    }
    if (score <= 4) {
      return { score, label: 'Medium', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400' }
    }
    return { score, label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400' }
  }

  const passwordStrength = getPasswordStrength(password)

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      setLoading(false)
      return
    }

    try {
      const captchaToken = await captcha.getToken()
      // With email confirmation off, signUp signs the user in here (and loads the next page below).
      const { error } = await signInOrOutHere(() => supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${getSiteURL()}/auth/callback`,
          captchaToken,
        },
      }))

      if (error) {
        const isCaptchaError =
          error.code === 'captcha_failed' ||
          (error.code === 'unexpected_failure' &&
            error.message?.toLowerCase().includes('captcha'))
        if (isCaptchaError) {
          console.warn('[captcha] verification failed', {
            code: error.code,
            message: error.message?.slice(0, 120),
          })
          setError('Bot check failed — please click Continue again.')
        } else if (error.message.toLowerCase().includes('rate limit')) {
          setError('Too many signup attempts. Please wait a few minutes and try again, or contact support if this persists.')
        } else {
          setError(error.message)
        }
      } else {
        const referralCode = getStoredReferralCode()
        // $set / $set_once become person properties — last-touch for
        // attributed_referral_code, set-once for first_referral_code_seen.
        // Only set when we actually have a code so non-ambassador signups
        // don't carry a `null` person property.
        captureEvent(EVENTS.SIGNUP_COMPLETED, {
          method: 'email',
          has_share_id: !!shareId,
          ...(referralCode
            ? {
                referral_code: referralCode,
                $set: { attributed_referral_code: referralCode },
                $set_once: { first_referral_code_seen: referralCode },
              }
            : {}),
        })
        // Arms the post-signup welcome dialog (cleared on first display in
        // the (app) layout). Survives the OAuth/checkout round-trip via
        // localStorage; if the user bails before reaching the app, the
        // dialog fires on their first authed visit instead.
        armWelcomeTour()
        // Before the page unloads, so the call is sent; a failure never blocks the sign-up.
        await pushSignupToLoops().catch(() => {})
        // If there's a pending checkout, go to the checkout pending page
        // Otherwise go to the redirect target or pricing. redirectTo is already
        // sanitized at the server boundary via safeRedirect, but we belt-and-brace
        // here too for any future client-side mutation path.
        const pending = localStorage.getItem('pending_checkout')
        if (pending) {
          window.location.href = '/checkout/pending'
        } else {
          window.location.href = safeRedirect(redirectTo, '/pricing')
        }
      }
    } catch (_err) {
      setError('An unexpected error occurred')
    } finally {
      // Remount the widget so a retry gets a fresh single-use token (the prior
      // one was consumed by Supabase). Button is gated on `loading` only, so it
      // never gets stuck waiting for a token.
      captcha.refresh()
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    if (loading) return; // Prevent double-click

    setError(null)
    setLoading(true)

    try {
      // Arms the post-signup welcome dialog ahead of the OAuth round-trip —
      // localStorage survives the redirect back to /auth/callback. Cleared on
      // first display in the (app) layout, so existing users re-signing in
      // via Google never see it (the flag is only set here at signup time).
      armWelcomeTour();
      // Cookie-based intent — survives the Google→Supabase→callback roundtrip with
      // SameSite=Lax. A query string on the callback URL would be stripped by Supabase's
      // strict OAuth redirect-URI allowlist. See lib/auth/oauth-intent.
      // Post-auth target is always same-origin (`/checkout/pending` or `safeRedirect` output)
      // so the Google callback can never be tricked into bouncing offsite.
      const pending = localStorage.getItem('pending_checkout');
      const postAuthTarget = pending ? '/checkout/pending' : safeRedirect(redirectTo, '/pricing');
      setOAuthIntent(postAuthTarget);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getSiteURL()}/auth/callback`,
        },
      })

      if (error) {
        setError(error.message)
        setLoading(false)
      }
      // If successful, user will be redirected to Google
    } catch (_err) {
      setError('An unexpected error occurred')
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen bg-bg dark:bg-bg-primary">
      <div className="relative mx-auto w-full max-w-6xl min-h-screen px-4 sm:px-6 lg:px-8 flex flex-col md:grid md:grid-cols-2">
        {/* Way home — sits over the proof panel on desktop, above the card on mobile. */}
        <Link
          href="/"
          aria-label="Sharpr home"
          className="absolute left-4 sm:left-6 lg:left-8 top-5 md:top-8 z-10 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
        >
          {/* Icon on phones, full wordmark on desktop. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/sharpr-icon.png" alt="Sharpr" className="h-8 w-8 md:hidden" />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/sharpr.png" alt="Sharpr" {...SHARPR_WORDMARK_DIMS} className="hidden md:block h-10 w-auto" />
        </Link>
        <WelcomePanel />
        <main className="flex-1 flex items-center justify-center pt-16 pb-4 md:py-16 md:pl-10 lg:pl-16">
          <div className="w-full max-w-md">
            {/* Signup Card */}
            <div className="bg-white dark:bg-bg-secondary rounded-lg shadow-lg p-5 md:p-8 border border-border dark:border-border-secondary">
              {/* Card Header — branded H1 on mobile (welcome panel is hidden there),
                  "Create your account" on desktop (welcome panel carries the hero H1). */}
              <div className="mb-5 md:mb-7 text-center">
                <h1 className="md:hidden text-2xl font-semibold tracking-tight text-balance text-fg dark:text-text-primary">
                  Find the{' '}
                  <img
                    src="/sharpr.png"
                    alt="Sharpr"
                    className="inline-block h-8 w-auto align-[-0.35em]"
                  />{' '}
                  edge
                </h1>
                <h2 className="hidden md:block text-xl md:text-2xl font-bold text-fg dark:text-text-primary">
                  Create your account
                </h2>
              </div>
              {/* Error Message */}
              {error && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                >
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Google Signup */}
              <button
                onClick={handleGoogleSignUp}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 md:py-3 mb-4 md:mb-6 bg-white dark:bg-bg-tertiary text-fg dark:text-text-primary border border-border dark:border-border-secondary rounded-md hover:bg-hover dark:hover:bg-bg-overlay transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className="relative mb-4 md:mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border dark:border-border-secondary"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-bg-secondary text-muted-foreground dark:text-text-tertiary">
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleEmailSignUp} className="space-y-3 md:space-y-4">
                <div>
                  <label htmlFor="email" className={LABEL_CLASS}>
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      // Clear any stale suggestion the moment the user edits; we'll
                      // re-check on blur if the new value still looks like a typo.
                      if (emailSuggestion) setEmailSuggestion(null)
                    }}
                    onBlur={() => setEmailSuggestion(suggestEmailFix(email))}
                    required
                    autoComplete="username"
                    inputMode="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-describedby={emailSuggestion ? 'email-suggestion' : undefined}
                    className={INPUT_CLASS}
                    placeholder="you@example.com"
                    disabled={loading}
                  />
                  {emailSuggestion && (
                    <p
                      id="email-suggestion"
                      role="status"
                      aria-live="polite"
                      className="mt-1.5 text-xs text-muted-foreground dark:text-text-tertiary"
                    >
                      Did you mean{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setEmail(emailSuggestion)
                          setEmailSuggestion(null)
                        }}
                        className="font-medium text-brand-500 dark:text-brand-400 hover:underline"
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </p>
                  )}
                </div>

                <PasswordField
                  id="password"
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  visible={showPassword}
                  onToggleVisible={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {password && (
                    <div className="mt-2">
                      {/* Strength Meter */}
                      <div className="flex gap-1 mb-1">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all duration-200 ${
                              i < passwordStrength.score
                                ? passwordStrength.color
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground dark:text-text-tertiary">
                        Password strength:{' '}
                        <span className={`font-medium ${passwordStrength.textColor}`}>
                          {passwordStrength.label}
                        </span>
                      </p>
                    </div>
                  )}
                </PasswordField>

                <div className="flex justify-center">
                  <CaptchaWidget
                    key={captcha.renderKey}
                    ref={captcha.ref}
                    onSuccess={captcha.setToken}
                    onExpire={captcha.refresh}
                    onError={captcha.refresh}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2.5 md:py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 active:scale-[0.98] text-white rounded-md font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {getSubmitLabel({ loading })}
                </button>
              </form>

              {/* Login Link */}
              <div className="mt-4 md:mt-6 text-center">
                <p className="text-sm text-muted-foreground dark:text-text-secondary">
                  Already have an account?{' '}
                  <Link
                    href={loginUrl}
                    className="text-brand-500 dark:text-brand-400 hover:underline font-medium"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
